import React from "react"
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    UniqueIdentifier,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { SortableItem } from "./SortableItem"

type SortableListProps<T> = {
    items: T[]
    onChange: (items: T[]) => void
    renderItem: (item: T) => React.ReactNode
    keyExtractor: (item: T) => UniqueIdentifier // We need to know how to get a unique ID
    onClick?: (item: T) => void
    className?: string
}

/**
 * Base, non-styled component for sortable lists
 * If you want to use styled component with drag handlers, use <SortableListStyled />
 */
export const SortableList = <T,>({
    items,
    onChange,
    renderItem,
    keyExtractor,
    onClick,
    className,
}: SortableListProps<T>) => {
    const sensors = useSensors(
        // Start dragging when the pointer moves at least 8px to avoid accidental drags/clicks
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        // Add support for keyboard dragging - 'tab' highlights the element and 'space' allow the dragging
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
            const oldIndex = items.findIndex(item => keyExtractor(item) === active.id)
            const newIndex = items.findIndex(item => keyExtractor(item) === over.id)

            onChange(arrayMove(items, oldIndex, newIndex))
        }
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
                // We map the objects to their IDs for dnd-kit
                items={items.map(item => keyExtractor(item))}
                strategy={verticalListSortingStrategy}>
                {/* We map the objects to the UI */}
                {items.length > 0 ? (
                    <ul style={{ listStyle: "none", padding: 0 }} className={className}>
                        {items.map(item => (
                            <SortableItem
                                key={keyExtractor(item)}
                                id={keyExtractor(item)}
                                item={item}
                                onClick={onClick}>
                                {renderItem(item)}
                            </SortableItem>
                        ))}
                    </ul>
                ) : null}
            </SortableContext>
        </DndContext>
    )
}
